import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaStore, FaSpinner, FaImage, FaExternalLinkAlt } from 'react-icons/fa';
import formatCurrency from '../../utils/formatCurrency';
import formatAplicaciones from '../../utils/formatAplicaciones';
import { validarUbicacion } from '../ubicacionValidator';
import { storeProductUrl } from '../../utils/storeConstants';

const CROSS_SELL_LIMIT = 4;

const openProductVista = (id) => {
    window.open(`/productos/vista/${id}`, '_blank', 'noopener,noreferrer');
};

const CrossSellPanel = ({ productId, token }) => {
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!productId) return;

        let cancelled = false;

        const fetchRecommendations = async () => {
            setLoading(true);
            try {
                const res = await axios.get(
                    `/api/productos/${productId}/cross-selling?limit=${CROSS_SELL_LIMIT}`,
                    token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
                );
                if (!cancelled) {
                    setRecommendations(Array.isArray(res.data) ? res.data : []);
                }
            } catch (error) {
                console.error('Error fetching cross-sell recommendations:', error);
                if (!cancelled) setRecommendations([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchRecommendations();
        return () => { cancelled = true; };
    }, [productId, token]);

    if (loading) {
        return (
            <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex justify-center py-10">
                    <FaSpinner className="animate-spin text-yellow-500 text-3xl" />
                </div>
            </div>
        );
    }

    if (!recommendations.length) {
        return (
            <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
                    <span className="w-1.5 h-6 bg-yellow-400 rounded-full mr-3 shrink-0" />
                    Productos sugeridos para ofrecer al cliente
                </h3>
                <p className="text-sm text-gray-500 italic ml-5">
                    Sin sugerencias para este producto por ahora.
                </p>
            </div>
        );
    }

    return (
        <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-800 flex items-center">
                    <span className="w-1.5 h-6 bg-yellow-400 rounded-full mr-3 shrink-0" />
                    Productos sugeridos para ofrecer al cliente
                </h3>
                <p className="text-sm text-gray-500 mt-1 ml-5">
                    Basado en compras frecuentes y reglas de recomendación
                </p>
            </div>

            <div className="space-y-3">
                {recommendations.map((item) => {
                    const displayPrice = item.es_padre && item.min_precio_hijos != null
                        ? item.min_precio_hijos
                        : item.precio_venta;
                    const aplicacionTexto = formatAplicaciones(item.aplicaciones);

                    return (
                        <div
                            key={item.id_producto}
                            className="flex flex-col sm:flex-row sm:items-start gap-3 p-3 sm:p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:border-yellow-300 hover:bg-yellow-50/30 transition-all group"
                        >
                            <button
                                type="button"
                                onClick={() => openProductVista(item.id_producto)}
                                className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg border border-gray-100 flex-shrink-0 flex items-center justify-center overflow-hidden shadow-sm self-start"
                                title="Ver ficha del producto"
                            >
                                {item.imagen_url ? (
                                    <img
                                        src={item.imagen_url}
                                        alt={item.nombre || item.nombre_producto}
                                        className="w-full h-full object-contain p-1"
                                    />
                                ) : (
                                    <FaImage className="text-gray-300 text-xl" />
                                )}
                            </button>

                            <div className="flex-1 min-w-0 space-y-1">
                                <button
                                    type="button"
                                    onClick={() => openProductVista(item.id_producto)}
                                    className="text-left w-full"
                                >
                                    <p className="text-sm font-bold text-gray-900 line-clamp-2 group-hover:text-indigo-700 transition-colors">
                                        {item.nombre || item.nombre_producto}
                                    </p>
                                </button>

                                <p className="text-xs text-gray-600">
                                    <span className="font-semibold">SKU:</span> {item.sku}
                                    {item.codigo_barras && (
                                        <>
                                            <span className="text-gray-300 mx-1">·</span>
                                            <span className="font-semibold">Cód. Barras:</span> {item.codigo_barras}
                                        </>
                                    )}
                                </p>

                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600">
                                    {item.marca && (
                                        <span><span className="font-semibold">Marca:</span> {item.marca}</span>
                                    )}
                                    {item.medidas && (
                                        <span><span className="font-semibold">Medidas:</span> {item.medidas}</span>
                                    )}
                                    {item.ubicacion && (
                                        <span><span className="font-semibold">Ubicación:</span> {validarUbicacion(item.ubicacion)}</span>
                                    )}
                                    {item.categoria && (
                                        <span><span className="font-semibold">Categoría:</span> {item.categoria}</span>
                                    )}
                                </div>

                                <p className="text-xs text-gray-600">
                                    <span className="font-semibold">Aplicación:</span>{' '}
                                    <span className="italic">{aplicacionTexto}</span>
                                </p>

                                <div className="flex flex-wrap items-center gap-3 pt-1">
                                    {item.existencias != null && (
                                        <span className="text-xs">
                                            <span className="font-semibold text-gray-700">Existencias:</span>{' '}
                                            <span className="font-bold bg-black text-yellow-400 px-2 py-0.5 rounded-full">
                                                {parseFloat(item.existencias).toFixed(1)}
                                            </span>
                                        </span>
                                    )}
                                    {item.stock_minimo != null && (
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">Stock Mín:</span> {item.stock_minimo}
                                        </span>
                                    )}
                                    <span className="text-sm font-bold text-green-600">
                                        <span className="text-gray-700 font-semibold text-xs">Precio: </span>
                                        {item.es_padre ? (
                                            <>Desde: {formatCurrency(displayPrice)}</>
                                        ) : (
                                            formatCurrency(displayPrice)
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className="flex sm:flex-col gap-2 shrink-0 self-start sm:self-center">
                                <button
                                    type="button"
                                    onClick={() => openProductVista(item.id_producto)}
                                    className="bg-white hover:bg-indigo-600 hover:text-white p-2.5 rounded-xl border border-gray-200 shadow-sm transition-all flex items-center justify-center"
                                    title="Ver ficha en nueva pestaña"
                                >
                                    <FaExternalLinkAlt className="text-sm" />
                                </button>
                                <a
                                    href={storeProductUrl(item.id_producto)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="bg-white hover:bg-gray-900 hover:text-white p-2.5 rounded-xl border border-gray-200 shadow-sm transition-all flex items-center justify-center"
                                    title="Ver en tienda virtual"
                                >
                                    <FaStore className="text-sm" />
                                </a>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CrossSellPanel;
